<template>
	<div>
		<core-layout-headline-with-tabs :tabs="tabs">
			{{ $t("resource") }}
		</core-layout-headline-with-tabs>

		<core-layout-content full>
			<!-- Language selector -->
			<div class="xs-4 mb4" v-if="$store.state.settings.allowedLocales">
				<div class="pr2 xs-12 fs3 lh125 fw7 pb1">{{ $t("language") }}</div>
				<core-select-locale
					class="xs-12"
					size="xsmall"
					v-model="selectedLocale"
				/>
			</div>

			<div class="fs5 fw7 pb4 pt2">{{ computedResourceName || "---" }}</div>

			<div v-if="isLoading" class="xs-12 flex xs-middle xs-center">
				<j-spinner />
			</div>

			<!-- Toggle editor/preview -->
			<div v-if="selectedPage">
				<j-tabs
					:selectedTab="editorOrPreview"
					:tabs="[
						{
							name: $t('editor'),
							value: 'editor'
						},
						{
							name: $t('preview'),
							value: 'preview'
						}
					]"
					@click="onChangeMode"
				/>
			</div>
			<j-card class="flex wrap bg5 xs-12">
				<!-- Resource -->
				<div class="xs-12">
					<!-- Pages -->
					<div class="p4">
						<div class="fs4 fw7 pb2">{{ $t("pages") }}</div>
						<div class="flex xs-between xs-middle">
							<div v-if="pages.length" class="flex">
								<div
									v-for="(page, i) in pages"
									:key="i"
									class="p2 fw7 fs3 cu2 b1 bc7 mr2"
									:class="[
										{ c6: i !== selectedPageIndex },
										{ 'bg1 c5': i === selectedPageIndex }
									]"
									@click="selectedPageIndex = i"
								>
									{{ i + 1 }}
								</div>
							</div>
							<div v-else class="c6 fs3">
								{{ $t("errors.no_data") }}
							</div>
							<div>
								<j-button size="xsmall" class="mr2" @click="addPageToResource"
									>+ {{ $t("add_page") }}</j-button
								>
							</div>
						</div>
					</div>

					<!-- Page content -->
					<div class="bt1 bc7">
						<page-editor
							v-if="selectedPage && editorOrPreview === 'editor'"
							:page="selectedPage"
							:resourceId="id"
							:index="selectedPageIndex + 1"
							:selectedLocale="selectedLocale"
							@page:updated="fetchResource"
							@page:removed="onPageRemoved"
						/>
						<page-preview
							v-if="selectedPage && editorOrPreview === 'preview'"
							:page="selectedPage"
							:resourceId="id"
						/>
					</div>
				</div>
			</j-card>
		</core-layout-content>
	</div>
</template>

<script>
/*
 * Read resources/readme.md to understand why we use courses API and what are the differences with Education module
 */
import CoreLayoutContent from "@/modules/core/components/CoreLayoutContent"
import CoreLayoutHeadlineWithTabs from "@/modules/core/components/CoreLayoutHeadlineWithTabs"
import CoreSelectLocale from "@/modules/core/components/CoreSelectLocale"
import JCard from "@/components/JCard"
import JSpinner from "@/components/JSpinner"
import JButton from "@/components/JButton"
import JTabs from "@/components/JTabs"
import PageEditor from "../components/PageEditor"
import PagePreview from "../components/PagePreview"

import api from "@/api/gql.js"
import queries from "../utils/queries.js"
import { i18n } from "@/locales/setup.js"

export default {
	components: {
		CoreLayoutContent,
		CoreLayoutHeadlineWithTabs,
		CoreSelectLocale,
		JCard,
		JSpinner,
		JButton,
		JTabs,
		PageEditor,
		PagePreview
	},

	props: {
		// Course Id
		id: String
	},

	computed: {
		pages() {
			return this.resource?.sections || []
		},
		selectedPage() {
			return this.pages?.[this.selectedPageIndex]
		},
		computedResourceName() {
			return this.resource?.name
		}
	},

	data() {
		return {
			isLoading: false,
			resource: null,
			selectedLocale: i18n.locale,
			selectedPageIndex: 0,
			editorOrPreview: "editor",
			tabs: [
				{
					id: "overview",
					name: this.$t("overview"),
					to: { name: "ResourceDetail", params: { ...this.$route.params } }
				},
				{
					id: "content",
					name: this.$t("content"),
					to: {
						name: "ResourceContent",
						params: { ...this.$route.params }
					}
				}
			]
		}
	},

	methods: {
		onChangeMode(payload) {
			this.editorOrPreview = payload
			this.fetchResource()
		},

		onPageRemoved() {
			if (this.selectedPageIndex >= this.pages.length - 1)
				this.selectedPageIndex--
			this.fetchResource()
		},

		async fetchResource() {
			this.isLoading = true
			const res = await api({
				locale: this.selectedLocale,
				query: queries.fetchCourse,
				variables: {
					where: { id: this.id }
				}
			})
			this.isLoading = false

			// Check errors
			if (res.errors && res?.errors?.length)
				this.$store.dispatch("showError", [res.errors?.[0]?.message])

			this.resource = res?.data?.courses?.list[0]
		},

		async addPageToResource() {
			this.isLoading = true
			/*
			 * Add section to course
			 */
			let variables = {
				courseId: this.id
			}

			let res = await api({ query: queries.addSectionToCourse, variables })

			if (res?.data?.addSectionToCourse?.status !== "success") {
				this.$store.dispatch("addMessage", {
					message: {
						type: "error",
						text: this.$t("errors.general")
					}
				})
				this.isLoading = false
				return
			}
			this.isLoading = false
			this.selectedPageIndex = this.pages.length
			this.fetchResource()
		}
	},

	created() {
		this.fetchResource()
	},

	watch: {
		selectedLocale: {
			handler(o, n) {
				if (o && n) {
					this.fetchResource()
				}
			}
		}
	}
}
</script>
